import type { AxiosRequestConfig } from 'axios';

/**
 * 로그인 기기 식별자 — 운영 계정(Admin·정산 관리자) 2단계 인증용.
 * 백엔드 스펙: smartbugo-api/docs/admin-login-phone-verification.md
 *
 * 한 번 휴대폰 인증을 마친 브라우저를 30일간 다시 묻지 않기 위해 서버가 기기를 구분하는 값이다.
 * 브라우저마다 한 번 만들어 localStorage에 두고 계속 쓴다.
 *
 * 이 값이 없거나 지워져도(시크릿 모드·캐시 삭제) 로그인이 막히지는 않는다.
 * 서버가 기억을 못 할 뿐이라 다음 로그인에 인증을 한 번 더 하게 된다.
 */

//* 백엔드 login-device-id.header.ts 와 같은 이름이어야 한다
export const LOGIN_DEVICE_ID_HEADER = 'x-login-device-id';

const LOGIN_DEVICE_ID_KEY = 'loginDeviceId';

//* 서버가 받아주는 형식 — 이 형식이 아니면 서버가 없는 것으로 취급한다
const isValidDeviceId = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Za-z0-9_-]{8,64}$/.test(value);

//* uuid v4. 구형 브라우저·비보안 컨텍스트(randomUUID 없음)까지 폴백을 둔다
const createDeviceId = (): string => {
  const cryptoRef: Crypto | undefined = window.crypto;
  if (typeof cryptoRef?.randomUUID === 'function') return cryptoRef.randomUUID();
  if (typeof cryptoRef?.getRandomValues === 'function') {
    const bytes = cryptoRef.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * 이 브라우저의 기기 ID. 없으면 만들어 저장한다.
 * 저장이 막힌 환경에서는 null (헤더를 싣지 않는다 = 기억되지 않음).
 */
export const getLoginDeviceId = (): string | null => {
  try {
    const stored = localStorage.getItem(LOGIN_DEVICE_ID_KEY);
    if (isValidDeviceId(stored)) return stored;

    const created = createDeviceId();
    if (!isValidDeviceId(created)) return null;
    localStorage.setItem(LOGIN_DEVICE_ID_KEY, created);
    return created;
  } catch (e) {
    //* 시크릿 모드 등 저장 차단 — 인증은 되고 기억만 안 된다
    return null;
  }
};

/**
 * 기기 ID 헤더를 실어야 하는 요청인지 — **운영 콘솔의 아이디·비밀번호 로그인과 로그인 문의만.**
 *
 * 서버는 이 헤더가 없으면 기기 잠금(실패 횟수 세기·429)을 통째로 건너뛴다. 고객(상주·구매자)은
 * 잠겨선 안 되므로 고객 경로에는 싣지 않는다:
 * - 게스트 로그인(`auth/login/guest/*`, `signup-or-login/guest/*`)·카카오 로그인은 결제·조회 중에
 *   자동으로 타는 고객 경로라 목록에 넣지 않는다.
 * - 같은 username-password 엔드포인트라도 고객 화면(화환 스토어 로그인 등)에서 부르면 호출부가
 *   `skipLoginDeviceId`(→ `withoutLoginDeviceId()`)로 빼고 보낸다.
 * (challenge JWT 안에 기기 ID가 들어가서 휴대폰 인증 send/confirm 은 헤더가 필요 없다.)
 */
export const isLoginEndpoint = (url?: string): boolean =>
  !!url &&
  (url.includes('auth/login/username-password') ||
    //* 로그인 문의 — Slack 알림에 기기 앞 8자리가 찍혀 잠금 해제 페이지에서 바로 찾는다
    url.includes('public/enquiry/login-help'));

/**
 * 고객 화면에서 로그인 계열 요청을 보낼 때 axios config에 얹는 제외 표식.
 * 헤더가 안 실리면 서버는 기기 잠금을 적용하지 않는다(세지도, 막지도 않는다).
 * 예: `api_.post(url, body, withoutLoginDeviceId())`
 */
export const withoutLoginDeviceId = (): AxiosRequestConfig => ({
  skipLoginDeviceId: true,
});

//* axios config 에 요청 단위 표식을 얹기 위한 타입 확장 (인터셉터 attachLoginDeviceId 가 읽는다)
declare module 'axios' {
  export interface AxiosRequestConfig {
    /** true 면 x-login-device-id 를 싣지 않는다 — 고객 화면의 로그인 계열 호출 */
    skipLoginDeviceId?: boolean;
  }
}

// ---- 잠금 캐시 -------------------------------------------------------------------
//
// 잠금의 진실은 서버 문서다. 여기 저장하는 값은 새로고침 뒤에도 로그인 화면이 카운트다운을
// 그리고, 잠긴 동안 서버를 두드리지 않기 위한 캐시일 뿐이다. 프론트가 스스로 잠그는 일은 없고,
// 지운다고 풀리지도 않는다 (전화 해제는 관리자 화면에서만).

const LOGIN_DEVICE_LOCK_KEY = 'loginDeviceLock';

export interface StoredLoginDeviceLock {
  /** ISO — 서버가 준 잠금 해제 예정 시각 */
  lockedUntil: string;
}

export const storeLoginDeviceLock = (lock: StoredLoginDeviceLock) => {
  try {
    localStorage.setItem(LOGIN_DEVICE_LOCK_KEY, JSON.stringify(lock));
  } catch (e) {
    //* 저장 불가 환경 — 서버가 계속 429 를 주므로 동작은 같다
  }
};

export const clearLoginDeviceLock = () => {
  try {
    localStorage.removeItem(LOGIN_DEVICE_LOCK_KEY);
  } catch (e) {
    //* ignore
  }
};

/** 아직 만료되지 않은 잠금 캐시. 만료됐으면 지우고 null */
export const getActiveLoginDeviceLock = (): StoredLoginDeviceLock | null => {
  try {
    const raw = localStorage.getItem(LOGIN_DEVICE_LOCK_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredLoginDeviceLock>;
    const until = parsed?.lockedUntil ? new Date(parsed.lockedUntil).getTime() : NaN;
    if (!Number.isFinite(until) || until <= Date.now()) {
      clearLoginDeviceLock();
      return null;
    }
    return { lockedUntil: parsed.lockedUntil as string };
  } catch (e) {
    return null;
  }
};
